import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@patient-watch/ui/alert-dialog";
import { Label } from "@patient-watch/ui/label";
import { Switch } from "@patient-watch/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
    useDeleteDictionary,
    useDeleteDictionaryOption,
    useDictionaryDetail,
    useUpdateDictionary,
    useUpsertDictionaryOption,
} from "@/features/dictionaries/api/dictionaryBuilderApi";
import type { Database } from "@/types/database/database";
import { useAuth } from "@/utils/auth";

type OptionRow = Database["public"]["Tables"]["options"]["Row"];

/** A text field that saves when it loses focus. */
function BlurInput({
    value,
    onCommit,
    disabled,
    className,
    placeholder,
    ariaLabel,
}: {
    value: string;
    onCommit: (next: string) => void;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
    ariaLabel?: string;
}) {
    const [draft, setDraft] = useState(value);
    useEffect(() => setDraft(value), [value]);
    return (
        <ui.Input
            value={draft}
            disabled={disabled}
            placeholder={placeholder}
            aria-label={ariaLabel}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => draft !== value && onCommit(draft)}
            className={className}
        />
    );
}

const optionValueText = (value: unknown) =>
    value == null ? "" : typeof value === "string" ? value : JSON.stringify(value);

/** Option values are numbers where they look like numbers, so scoring keeps working. */
const parseOptionValue = (text: string) => {
    const trimmed = text.trim();
    if (trimmed === "") return null;
    return Number.isFinite(Number(trimmed)) ? Number(trimmed) : trimmed;
};

const DictionaryEditor = () => {
    const { dictionaryId } = Route.useParams();
    const auth = useAuth();
    const canEdit = Boolean(auth?.roles?.isAdmin || auth?.isSuperUser);
    const navigate = useNavigate();
    const { data, isLoading } = useDictionaryDetail(dictionaryId);
    const updateDictionary = useUpdateDictionary(dictionaryId);
    const deleteDictionary = useDeleteDictionary();
    const upsertOption = useUpsertDictionaryOption(dictionaryId);
    const deleteOption = useDeleteDictionaryOption();

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Dictionaries", route: { to: "/builder/dictionaries" } },
        {
            label: data?.dictionary.name ?? "Dictionary",
            route: { to: "/builder/dictionaries/$dictionaryId", params: { dictionaryId } },
        },
    ]);

    if (isLoading || !data) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
        );
    }

    const { dictionary, options, usedBy } = data;
    const saveOption = (option: OptionRow, changes: Partial<OptionRow>) =>
        upsertOption.mutate({ ...option, ...changes });
    const move = (index: number, direction: -1 | 1) => {
        const current = options[index];
        const swap = options[index + direction];
        if (!current || !swap) return;
        saveOption(current, { sort_order: swap.sort_order });
        saveOption(swap, { sort_order: current.sort_order });
    };
    const nextSortOrder = options.reduce((max, option) => Math.max(max, option.sort_order ?? 0), 0) + 1;

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div className="flex justify-between items-start gap-4">
                <div className="min-w-0">
                    <h1 className="font-semibold text-3xl tracking-tight">{dictionary.name}</h1>
                    <p className="text-muted-foreground">
                        A shared answer list. Changes show in every question that uses it.
                    </p>
                </div>
                {canEdit ? (
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <ui.Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                disabled={usedBy.length > 0}
                            >
                                Delete dictionary
                            </ui.Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete {dictionary.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This deletes the dictionary and its {options.length} options. No question uses it.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={() =>
                                        deleteDictionary.mutate(dictionaryId, {
                                            onSuccess: () => void navigate({ to: "/builder/dictionaries" }),
                                        })
                                    }
                                >
                                    Delete
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                ) : null}
            </div>

            <div className="gap-6 grid lg:grid-cols-[minmax(0,1fr)_20rem]">
                <div className="space-y-6 min-w-0">
                    <ui.Card className="space-y-4 p-5">
                        <div className="gap-4 grid sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground text-xs">Name</Label>
                                <BlurInput
                                    value={dictionary.name ?? ""}
                                    disabled={!canEdit}
                                    onCommit={(name) => name.trim() && updateDictionary.mutate({ name: name.trim() })}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground text-xs">Description</Label>
                                <BlurInput
                                    value={dictionary.description ?? ""}
                                    disabled={!canEdit}
                                    onCommit={(description) =>
                                        updateDictionary.mutate({ description: description.trim() || null })
                                    }
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground text-xs">Placeholder</Label>
                                <BlurInput
                                    value={dictionary.placeholder ?? ""}
                                    disabled={!canEdit}
                                    placeholder="e.g. Choose a medication"
                                    onCommit={(placeholder) =>
                                        updateDictionary.mutate({ placeholder: placeholder.trim() || null })
                                    }
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-muted-foreground text-xs">No results text</Label>
                                <BlurInput
                                    value={dictionary.empty_text ?? ""}
                                    disabled={!canEdit}
                                    placeholder="e.g. No matches"
                                    onCommit={(empty_text) =>
                                        updateDictionary.mutate({ empty_text: empty_text.trim() || null })
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-6">
                            {(
                                [
                                    ["is_searchable", "Searchable"],
                                    ["is_clearable", "Clearable"],
                                    ["is_multiple", "Pick more than one"],
                                ] as const
                            ).map(([column, label]) => (
                                <label key={column} className="flex items-center gap-2 text-sm">
                                    <Switch
                                        checked={dictionary[column] === true}
                                        disabled={!canEdit}
                                        onCheckedChange={(checked) => updateDictionary.mutate({ [column]: checked })}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </ui.Card>

                    <ui.Card>
                        <div className="flex justify-between items-center px-5 py-4 border-b">
                            <div>
                                <p className="font-medium text-sm">Options</p>
                                <p className="text-muted-foreground text-xs">
                                    Values are saved with patients&apos; answers, so change labels freely but values
                                    with care. Hide an option rather than delete one patients have picked.
                                </p>
                            </div>
                            {canEdit ? (
                                <ui.Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                        upsertOption.mutate({
                                            label: "New option",
                                            value: nextSortOrder,
                                            code: nextSortOrder,
                                            sort_order: nextSortOrder,
                                        })
                                    }
                                >
                                    <Plus className="mr-1 w-4 h-4" />
                                    Add option
                                </ui.Button>
                            ) : null}
                        </div>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-16" />
                                    <TableHead>Label</TableHead>
                                    <TableHead className="w-28">Value</TableHead>
                                    <TableHead className="w-24">Code</TableHead>
                                    <TableHead className="w-36">Group</TableHead>
                                    <TableHead className="w-20">Shown</TableHead>
                                    <TableHead className="w-10" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {options.map((option, index) => (
                                    <TableRow key={option.option_id} className={option.is_disabled ? "opacity-60" : ""}>
                                        <TableCell>
                                            <div className="flex">
                                                <ui.Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="w-6 h-6"
                                                    aria-label="Move up"
                                                    disabled={!canEdit || index === 0}
                                                    onClick={() => move(index, -1)}
                                                >
                                                    <ChevronUp size={14} />
                                                </ui.Button>
                                                <ui.Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="w-6 h-6"
                                                    aria-label="Move down"
                                                    disabled={!canEdit || index === options.length - 1}
                                                    onClick={() => move(index, 1)}
                                                >
                                                    <ChevronDown size={14} />
                                                </ui.Button>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <BlurInput
                                                value={option.label ?? ""}
                                                disabled={!canEdit}
                                                ariaLabel="Label"
                                                className="h-8 text-sm"
                                                onCommit={(label) => saveOption(option, { label })}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <BlurInput
                                                value={optionValueText(option.value)}
                                                disabled={!canEdit}
                                                ariaLabel="Value"
                                                className="h-8 font-mono text-xs"
                                                onCommit={(text) =>
                                                    saveOption(option, { value: parseOptionValue(text) as never })
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <BlurInput
                                                value={option.code == null ? "" : String(option.code)}
                                                disabled={!canEdit}
                                                ariaLabel="Code"
                                                className="h-8 text-xs"
                                                onCommit={(text) =>
                                                    saveOption(option, {
                                                        code: text.trim() === "" ? null : Number(text) || null,
                                                    })
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <BlurInput
                                                value={option.group_label ?? ""}
                                                disabled={!canEdit}
                                                ariaLabel="Group"
                                                className="h-8 text-xs"
                                                onCommit={(group) =>
                                                    saveOption(option, { group_label: group.trim() || null })
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={option.is_disabled !== true}
                                                disabled={!canEdit}
                                                aria-label="Shown to patients"
                                                onCheckedChange={(shown) => saveOption(option, { is_disabled: !shown })}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {canEdit ? (
                                                <ui.Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="w-7 h-7"
                                                    aria-label="Delete option"
                                                    onClick={() => deleteOption.mutate(option.option_id)}
                                                >
                                                    <Trash2 size={14} />
                                                </ui.Button>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {options.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="py-10 text-muted-foreground text-center">
                                            No options yet.
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                    </ui.Card>
                </div>

                <ui.Card className="self-start p-5">
                    <p className="font-medium text-sm">Used by</p>
                    <p className="mb-3 text-muted-foreground text-xs">
                        Questions that take their choices from this dictionary.
                    </p>
                    {usedBy.length === 0 ? (
                        <p className="text-muted-foreground text-sm">
                            No questions yet. In the form builder, pick this dictionary under &quot;Choices from&quot;.
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {usedBy.map((usage) => (
                                <li key={usage.input_id} className="text-sm">
                                    {usage.form_id ? (
                                        <Link
                                            to="/builder/forms/$formId/$tab"
                                            params={{ formId: usage.form_id, tab: "questions" }}
                                            search={{ inputId: usage.input_id }}
                                            className="hover:underline"
                                        >
                                            <span className="block font-medium">{usage.form_name}</span>
                                            <span className="text-muted-foreground">{usage.input_label}</span>
                                        </Link>
                                    ) : (
                                        usage.input_label
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </ui.Card>
            </div>
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/dictionaries/$dictionaryId")({
    component: DictionaryEditor,
});
